/**
 * CL-DRAWINGS-007B — Project drawing category / subcategory taxonomy (pure).
 *
 * Saved on pmProjects.drawingTaxonomy. Drawing refs: drawingCategoryId, drawingSubcategoryId, drawingSortOrder.
 */

import {
  resolveDrawingDiscipline,
  resolveDrawingDisciplineKey,
  resolveDrawingSetLabel,
} from "./drawingOrganizationModel";
import { compareDrawingFiles } from "./drawingOrderModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

export const DRAWING_TAXONOMY_VERSION = 1;

export const DRAWING_TAXONOMY_UNCATEGORIZED_LABEL = "Uncategorized";

/** Virtual bucket — not stored as a category row. */
export const DRAWING_TAXONOMY_UNCATEGORIZED_ID = "__uncategorized__";

export const DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID = "__uncategorized_sub__";

const DISCIPLINE_LABELS = Object.freeze({
  architectural: "Architectural",
  structural: "Structural",
  civil: "Civil / survey",
  mechanical: "Mechanical / HVAC",
  electrical: "Electrical",
  plumbing: "Plumbing",
  landscape: "Landscape / site works",
  general: "General",
});

export function isMeaninglessSetLabel(label = "") {
  const s = lower(label);
  return !s || s === "none" || s === "uncategorized" || s === "unclassified";
}

export function displaySetOrSubcategoryLabel(label = "") {
  if (isMeaninglessSetLabel(label)) return DRAWING_TAXONOMY_UNCATEGORIZED_LABEL;
  return safeStr(label);
}

export function newTaxonomyId(prefix = "dt") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** While typing — preserve internal spaces; collapse repeated whitespace only. */
export function sanitizeTaxonomyNameInput(name = "") {
  return String(name ?? "").replace(/\s+/g, " ");
}

/** Validation / persistence — trim ends after whitespace normalization. */
export function normalizeTaxonomyName(name = "") {
  return safeStr(sanitizeTaxonomyNameInput(name));
}

function draftCategoryIdForDisciplineKey(disciplineKey = "") {
  const key = lower(disciplineKey);
  if (!key) return DRAWING_TAXONOMY_UNCATEGORIZED_ID;
  return `draft_cat_${key}`;
}

function draftSubcategoryIdForLabel(categoryId = "", name = "") {
  const cat = safeStr(categoryId) || DRAWING_TAXONOMY_UNCATEGORIZED_ID;
  const label = lower(normalizeTaxonomyName(name)).replace(/\s+/g, "_");
  return `draft_sub_${cat}_${label || "unnamed"}`;
}

export function readSavedDrawingTaxonomy(project = null) {
  const raw = project?.drawingTaxonomy;
  if (!raw || typeof raw !== "object") return null;
  if (raw.saved !== true && !raw.savedAt) return null;
  return normalizeTaxonomyDocument(raw);
}

export function normalizeTaxonomyDocument(raw = {}) {
  const categories = (Array.isArray(raw.categories) ? raw.categories : [])
    .map((c, index) => ({
      id: safeStr(c?.id) || newTaxonomyId("cat"),
      name: normalizeTaxonomyName(c?.name),
      sortOrder: Number.isFinite(c?.sortOrder) ? c.sortOrder : index,
    }))
    .filter((c) => c.name);
  const subcategories = (Array.isArray(raw.subcategories) ? raw.subcategories : [])
    .map((s, index) => ({
      id: safeStr(s?.id) || newTaxonomyId("sub"),
      categoryId: safeStr(s?.categoryId),
      name: normalizeTaxonomyName(s?.name),
      sortOrder: Number.isFinite(s?.sortOrder) ? s.sortOrder : index,
    }))
    .filter((s) => s.name && s.categoryId);
  return {
    version: DRAWING_TAXONOMY_VERSION,
    saved: raw.saved === true,
    categories: sortTaxonomyCategories(categories),
    subcategories: sortTaxonomySubcategories(subcategories),
  };
}

export function sortTaxonomyCategories(categories = []) {
  return [...categories].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function sortTaxonomySubcategories(subcategories = [], categoryId = "") {
  const list = categoryId
    ? subcategories.filter((s) => s.categoryId === categoryId)
    : [...subcategories];
  return list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function buildDraftDrawingTaxonomyFromInventory(files = [], saved = null) {
  const base = saved ? normalizeTaxonomyDocument(saved) : { version: DRAWING_TAXONOMY_VERSION, saved: false, categories: [], subcategories: [] };
  const categoryByKey = new Map();
  base.categories.forEach((c) => categoryByKey.set(lower(c.name), c));

  const ensureCategory = (disciplineKey, fallbackName) => {
    const label =
      (disciplineKey && DISCIPLINE_LABELS[disciplineKey]) ||
      fallbackName ||
      DRAWING_TAXONOMY_UNCATEGORIZED_LABEL;
    const key = lower(label);
    if (categoryByKey.has(key)) return categoryByKey.get(key);
    const cat = {
      id: disciplineKey ? draftCategoryIdForDisciplineKey(disciplineKey) : newTaxonomyId("cat"),
      name: label,
      sortOrder: base.categories.length,
    };
    base.categories.push(cat);
    categoryByKey.set(key, cat);
    return cat;
  };

  const subKey = (categoryId, name) => `${categoryId}::${lower(name)}`;
  const subMap = new Map();
  base.subcategories.forEach((s) => subMap.set(subKey(s.categoryId, s.name), s));

  (Array.isArray(files) ? files : []).forEach((file) => {
    if (!file) return;
    const discKey = resolveDrawingDisciplineKey(file);
    const cat = ensureCategory(discKey, discKey ? resolveDrawingDiscipline(file) : "");
    const setRaw = resolveDrawingSetLabel(file);
    if (isMeaninglessSetLabel(setRaw)) return;
    const setName = displaySetOrSubcategoryLabel(setRaw);
    const sk = subKey(cat.id, setName);
    if (subMap.has(sk)) return;
    const sub = {
      id: draftSubcategoryIdForLabel(cat.id, setName),
      categoryId: cat.id,
      name: setName,
      sortOrder: base.subcategories.filter((x) => x.categoryId === cat.id).length,
    };
    base.subcategories.push(sub);
    subMap.set(sk, sub);
  });

  return {
    ...base,
    saved: false,
    categories: sortTaxonomyCategories(base.categories),
    subcategories: base.subcategories,
  };
}

export function getEffectiveDrawingTaxonomy(project = null, inventory = []) {
  const saved = readSavedDrawingTaxonomy(project);
  if (saved) return { ...saved, isDraft: false };
  return { ...buildDraftDrawingTaxonomyFromInventory(inventory), isDraft: true };
}

export function findCategoryById(taxonomy, categoryId) {
  const id = safeStr(categoryId);
  if (!id || id === DRAWING_TAXONOMY_UNCATEGORIZED_ID) return null;
  return (taxonomy?.categories || []).find((c) => c.id === id) || null;
}

export function findSubcategoryById(taxonomy, subcategoryId) {
  const id = safeStr(subcategoryId);
  if (!id || id === DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID) return null;
  return (taxonomy?.subcategories || []).find((s) => s.id === id) || null;
}

export function findCategoryByDisciplineKey(taxonomy, disciplineKey) {
  const key = lower(disciplineKey);
  if (!key) return null;
  const label = DISCIPLINE_LABELS[key] || key;
  return (taxonomy?.categories || []).find((c) => lower(c.name) === lower(label)) || null;
}

export function findSubcategoryByNameInCategory(taxonomy, categoryId, name) {
  const n = lower(normalizeTaxonomyName(name));
  if (!n || n === lower(DRAWING_TAXONOMY_UNCATEGORIZED_LABEL)) return null;
  return (taxonomy?.subcategories || []).find(
    (s) => s.categoryId === categoryId && lower(s.name) === n
  );
}

export function resolveLegacyCategoryId(file = {}, taxonomy = {}) {
  const explicit = safeStr(file?.drawingCategoryId);
  if (explicit && findCategoryById(taxonomy, explicit)) return explicit;
  const byDisc = findCategoryByDisciplineKey(taxonomy, resolveDrawingDisciplineKey(file));
  if (byDisc) return byDisc.id;
  return DRAWING_TAXONOMY_UNCATEGORIZED_ID;
}

export function resolveLegacySubcategoryId(file = {}, taxonomy = {}, categoryId = "") {
  const explicit = safeStr(file?.drawingSubcategoryId);
  if (explicit && findSubcategoryById(taxonomy, explicit)) {
    const sub = findSubcategoryById(taxonomy, explicit);
    if (!categoryId || sub.categoryId === categoryId) return explicit;
  }
  const catId = categoryId || resolveLegacyCategoryId(file, taxonomy);
  if (catId === DRAWING_TAXONOMY_UNCATEGORIZED_ID) return DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID;
  const setLabel = resolveDrawingSetLabel(file);
  if (isMeaninglessSetLabel(setLabel)) return DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID;
  const match = findSubcategoryByNameInCategory(taxonomy, catId, displaySetOrSubcategoryLabel(setLabel));
  return match ? match.id : DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID;
}

export function resolveDrawingTaxonomyPlacement(file = {}, taxonomy = {}) {
  const categoryId = resolveLegacyCategoryId(file, taxonomy);
  const subcategoryId = resolveLegacySubcategoryId(file, taxonomy, categoryId);
  const category =
    categoryId === DRAWING_TAXONOMY_UNCATEGORIZED_ID
      ? { id: categoryId, name: DRAWING_TAXONOMY_UNCATEGORIZED_LABEL, sortOrder: 99999 }
      : findCategoryById(taxonomy, categoryId);
  const subcategory =
    subcategoryId === DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID
      ? {
          id: subcategoryId,
          categoryId,
          name: DRAWING_TAXONOMY_UNCATEGORIZED_LABEL,
          sortOrder: 99999,
        }
      : findSubcategoryById(taxonomy, subcategoryId);
  return {
    categoryId,
    subcategoryId,
    categoryName: category?.name || DRAWING_TAXONOMY_UNCATEGORIZED_LABEL,
    subcategoryName: subcategory?.name || DRAWING_TAXONOMY_UNCATEGORIZED_LABEL,
    categorySortOrder: category?.sortOrder ?? 99999,
    subcategorySortOrder: subcategory?.sortOrder ?? 99999,
  };
}

export function validateTaxonomyDraft(draft = {}) {
  const errors = [];
  const categories = Array.isArray(draft.categories) ? draft.categories : [];
  const subcategories = Array.isArray(draft.subcategories) ? draft.subcategories : [];
  const catNames = new Set();
  categories.forEach((c) => {
    const name = normalizeTaxonomyName(c?.name);
    if (!name) errors.push("Category name is required.");
    const key = lower(name);
    if (key && catNames.has(key)) errors.push(`Duplicate category name: ${name}`);
    if (key) catNames.add(key);
  });
  const subNamesByCat = new Map();
  subcategories.forEach((s) => {
    const name = normalizeTaxonomyName(s?.name);
    if (!name) errors.push("Subcategory name is required.");
    const catId = safeStr(s?.categoryId);
    if (!catId || !categories.some((c) => c.id === catId)) {
      errors.push(`Subcategory "${name}" has invalid category.`);
    }
    const bucket = subNamesByCat.get(catId) || new Set();
    const key = lower(name);
    if (bucket.has(key)) errors.push(`Duplicate subcategory "${name}" in the same category.`);
    bucket.add(key);
    subNamesByCat.set(catId, bucket);
  });
  return { ok: errors.length === 0, errors };
}

export function reindexTaxonomySortOrders(draft = {}) {
  const categories = sortTaxonomyCategories(draft.categories || []).map((c, index) => ({
    ...c,
    sortOrder: index,
  }));
  const subcategories = [...(draft.subcategories || [])];
  const byCat = new Map();
  categories.forEach((c) => byCat.set(c.id, []));
  subcategories.forEach((s) => {
    const bucket = byCat.get(s.categoryId) || [];
    bucket.push(s);
    byCat.set(s.categoryId, bucket);
  });
  const nextSubs = [];
  categories.forEach((cat) => {
    sortTaxonomySubcategories(byCat.get(cat.id) || [], cat.id).forEach((sub, index) => {
      nextSubs.push({ ...sub, sortOrder: index });
    });
  });
  subcategories.forEach((s) => {
    if (!categories.some((c) => c.id === s.categoryId)) {
      nextSubs.push(s);
    }
  });
  return { ...draft, categories, subcategories: nextSubs };
}

export function buildDrawingTaxonomySaveDocument(draft = {}, { actorUid = "" } = {}) {
  const normalized = reindexTaxonomySortOrders(normalizeTaxonomyDocument(draft));
  const validation = validateTaxonomyDraft(normalized);
  if (!validation.ok) {
    throw new Error(validation.errors[0] || "Invalid taxonomy.");
  }
  return {
    ...normalized,
    saved: true,
    version: DRAWING_TAXONOMY_VERSION,
    savedByUid: actorUid || null,
  };
}

export function buildDrawingTaxonomyAssignmentPatch({
  categoryId = "",
  subcategoryId = "",
  taxonomy = {},
  drawingSortOrder = null,
} = {}) {
  const catId = safeStr(categoryId);
  const subId = safeStr(subcategoryId);
  const cat = findCategoryById(taxonomy, catId);
  const patch = {
    drawingCategoryId: catId && cat ? catId : null,
    drawingSubcategoryId: subId && subId !== DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID ? subId : null,
  };
  if (typeof drawingSortOrder === "number" && Number.isFinite(drawingSortOrder)) {
    patch.drawingSortOrder = drawingSortOrder;
  }
  if (cat) {
    const discKey = Object.entries(DISCIPLINE_LABELS).find(([, label]) => lower(label) === lower(cat.name))?.[0];
    if (discKey) {
      patch.manualLibraryDiscipline = discKey;
      patch.discipline = discKey;
    }
    const sub = findSubcategoryById(taxonomy, subId);
    if (sub && !isMeaninglessSetLabel(sub.name) && lower(sub.name) !== lower(DRAWING_TAXONOMY_UNCATEGORIZED_LABEL)) {
      patch.subcategory = sub.name;
    } else {
      patch.subcategory = null;
    }
  }
  return patch;
}

export function computeNextDrawingSortOrder(files = [], taxonomy = {}, categoryId = "", subcategoryId = "") {
  const placements = (Array.isArray(files) ? files : []).filter((f) => {
    const p = resolveDrawingTaxonomyPlacement(f, taxonomy);
    return p.categoryId === categoryId && p.subcategoryId === subcategoryId;
  });
  let max = 0;
  placements.forEach((f) => {
    const n = typeof f?.drawingSortOrder === "number" ? f.drawingSortOrder : 0;
    if (n > max) max = n;
  });
  return max + 1;
}

export function sortFilesWithinTaxonomyBucket(files = [], project = null) {
  return [...(Array.isArray(files) ? files : [])].sort((a, b) => {
    const ao = typeof a?.drawingSortOrder === "number" ? a.drawingSortOrder : null;
    const bo = typeof b?.drawingSortOrder === "number" ? b.drawingSortOrder : null;
    if (ao != null && bo != null && ao !== bo) return ao - bo;
    if (ao != null && bo == null) return -1;
    if (ao == null && bo != null) return 1;
    return compareDrawingFiles(a, b);
  });
}

function isUncategorizedCategoryLabel(label) {
  return label === DRAWING_TAXONOMY_UNCATEGORIZED_LABEL;
}

function compareTaxonomyGroupSort(a, b) {
  if (isUncategorizedCategoryLabel(a.label)) return 1;
  if (isUncategorizedCategoryLabel(b.label)) return -1;
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return safeStr(a.id).localeCompare(safeStr(b.id));
}

function orderGroupedSubcategories(subMap, taxonomy = {}, categoryId = "", project = null) {
  const subs = [...subMap.values()].map((sub) => ({
    ...sub,
    files: sortFilesWithinTaxonomyBucket(sub.files, project),
    count: sub.files.length,
  }));
  if (taxonomy?.saved === true && categoryId && categoryId !== DRAWING_TAXONOMY_UNCATEGORIZED_ID) {
    const orderIndex = new Map();
    sortTaxonomySubcategories(taxonomy.subcategories || [], categoryId).forEach((s, index) => {
      orderIndex.set(s.id, index);
    });
    subs.sort((a, b) => {
      if (isUncategorizedCategoryLabel(a.label)) return 1;
      if (isUncategorizedCategoryLabel(b.label)) return -1;
      const ia = orderIndex.has(a.id) ? orderIndex.get(a.id) : 99998;
      const ib = orderIndex.has(b.id) ? orderIndex.get(b.id) : 99998;
      if (ia !== ib) return ia - ib;
      return safeStr(a.id).localeCompare(safeStr(b.id));
    });
    return subs;
  }
  subs.sort(compareTaxonomyGroupSort);
  return subs;
}

function orderGroupedCategories(categoryMap, taxonomy = {}, project = null) {
  const categories = [...categoryMap.values()];
  if (taxonomy?.saved === true) {
    const orderIndex = new Map();
    sortTaxonomyCategories(taxonomy.categories || []).forEach((c, index) => {
      orderIndex.set(c.id, index);
    });
    return categories
      .map((cat) => ({
        id: cat.id,
        label: cat.label,
        sortOrder: cat.sortOrder,
        subcategories: orderGroupedSubcategories(cat.subcategories, taxonomy, cat.id, project),
        count: cat.count,
      }))
      .sort((a, b) => {
        if (isUncategorizedCategoryLabel(a.label)) return 1;
        if (isUncategorizedCategoryLabel(b.label)) return -1;
        const ia =
          a.id === DRAWING_TAXONOMY_UNCATEGORIZED_ID
            ? 99999
            : orderIndex.has(a.id)
              ? orderIndex.get(a.id)
              : 99998;
        const ib =
          b.id === DRAWING_TAXONOMY_UNCATEGORIZED_ID
            ? 99999
            : orderIndex.has(b.id)
              ? orderIndex.get(b.id)
              : 99998;
        if (ia !== ib) return ia - ib;
        return safeStr(a.id).localeCompare(safeStr(b.id));
      });
  }
  return categories
    .map((cat) => ({
      id: cat.id,
      label: cat.label,
      sortOrder: cat.sortOrder,
      subcategories: orderGroupedSubcategories(cat.subcategories, taxonomy, cat.id, project),
      count: cat.count,
    }))
    .sort(compareTaxonomyGroupSort);
}

/**
 * Category → subcategory → drawings hierarchy for library presentation.
 */
export function groupDrawingsByTaxonomy(files = [], taxonomy = {}, { project = null } = {}) {
  const sortedPlacements = (Array.isArray(files) ? files : []).map((file) => ({
    file,
    placement: resolveDrawingTaxonomyPlacement(file, taxonomy),
  }));

  const categoryMap = new Map();
  sortedPlacements.forEach(({ file, placement }) => {
    const catKey = placement.categoryId;
    if (!categoryMap.has(catKey)) {
      categoryMap.set(catKey, {
        id: catKey,
        label: placement.categoryName,
        sortOrder: placement.categorySortOrder,
        subcategories: new Map(),
        count: 0,
      });
    }
    const cat = categoryMap.get(catKey);
    const subKey = placement.subcategoryId;
    if (!cat.subcategories.has(subKey)) {
      cat.subcategories.set(subKey, {
        id: subKey,
        label: placement.subcategoryName,
        sortOrder: placement.subcategorySortOrder,
        files: [],
      });
    }
    cat.subcategories.get(subKey).files.push(file);
    cat.count += 1;
  });

  const categories = orderGroupedCategories(categoryMap, taxonomy, project);

  return {
    categories,
    total: sortedPlacements.length,
  };
}

export function listTaxonomyCategoryFilterOptions(taxonomy = {}) {
  const options = sortTaxonomyCategories(taxonomy.categories || []).map((c) => ({
    value: c.id,
    label: c.name,
  }));
  options.unshift({ value: "all", label: "All categories" });
  options.push({ value: DRAWING_TAXONOMY_UNCATEGORIZED_ID, label: DRAWING_TAXONOMY_UNCATEGORIZED_LABEL });
  return options;
}

export function listTaxonomySubcategoryFilterOptions(taxonomy = {}, categoryId = "all") {
  if (!categoryId || categoryId === "all") {
    return [{ value: "all", label: "All subcategories" }];
  }
  const subs = sortTaxonomySubcategories(taxonomy.subcategories || [], categoryId).map((s) => ({
    value: s.id,
    label: s.name,
  }));
  subs.unshift({ value: "all", label: "All subcategories" });
  if (categoryId !== DRAWING_TAXONOMY_UNCATEGORIZED_ID) {
    subs.push({ value: DRAWING_TAXONOMY_UNCATEGORIZED_SUB_ID, label: DRAWING_TAXONOMY_UNCATEGORIZED_LABEL });
  }
  return subs;
}

export function fileMatchesDrawingTaxonomyFilters(
  file = {},
  taxonomy = {},
  { categoryKey = "all", subcategoryKey = "all", categoryId = "", subcategoryId = "" } = {}
) {
  const cat = safeStr(categoryKey || categoryId || "all") || "all";
  const sub = safeStr(subcategoryKey || subcategoryId || "all") || "all";
  if (cat === "all") return true;
  const p = resolveDrawingTaxonomyPlacement(file, taxonomy);
  if (p.categoryId !== cat) return false;
  if (sub !== "all" && p.subcategoryId !== sub) return false;
  return true;
}

export function filterFilesByTaxonomyFilters(
  files = [],
  taxonomy = {},
  { categoryKey = "all", subcategoryKey = "all", categoryId = "", subcategoryId = "" } = {}
) {
  const cat = safeStr(categoryKey || categoryId || "all") || "all";
  const sub = safeStr(subcategoryKey || subcategoryId || "all") || "all";
  if (cat === "all") return Array.isArray(files) ? files : [];
  return (Array.isArray(files) ? files : []).filter((file) =>
    fileMatchesDrawingTaxonomyFilters(file, taxonomy, { categoryKey: cat, subcategoryKey: sub })
  );
}

export function countDrawingsForTaxonomyTarget(inventory = [], taxonomy = {}, categoryId = "", subcategoryId = "") {
  let current = 0;
  let superseded = 0;
  let archived = 0;
  (Array.isArray(inventory) ? inventory : []).forEach((file) => {
    const p = resolveDrawingTaxonomyPlacement(file, taxonomy);
    if (p.categoryId !== categoryId) return;
    if (subcategoryId && p.subcategoryId !== subcategoryId) return;
    const status = lower(file?.status);
    const review = lower(file?.reviewStatus);
    if (status === "archived") archived += 1;
    else if (review === "superseded") superseded += 1;
    else current += 1;
  });
  return { current, superseded, archived, total: current + superseded + archived };
}

export function moveTaxonomyIds(draft, { type, id, direction, categoryId = "" }) {
  const next = {
    categories: [...(draft.categories || [])],
    subcategories: [...(draft.subcategories || [])],
  };
  if (type === "category") {
    const sorted = sortTaxonomyCategories(next.categories);
    const ix = sorted.findIndex((c) => c.id === id);
    if (ix < 0) return draft;
    const delta = direction === "up" ? -1 : 1;
    const j = ix + delta;
    if (j < 0 || j >= sorted.length) return draft;
    const reordered = [...sorted];
    [reordered[ix], reordered[j]] = [reordered[j], reordered[ix]];
    next.categories = reordered.map((c, index) => ({ ...c, sortOrder: index }));
    return reindexTaxonomySortOrders({ ...draft, ...next });
  }
  const subs = sortTaxonomySubcategories(next.subcategories, categoryId);
  const ix = subs.findIndex((s) => s.id === id);
  if (ix < 0) return draft;
  const delta = direction === "up" ? -1 : 1;
  const j = ix + delta;
  if (j < 0 || j >= subs.length) return draft;
  const reordered = [...subs];
  [reordered[ix], reordered[j]] = [reordered[j], reordered[ix]];
  const orderById = new Map(reordered.map((s, index) => [s.id, index]));
  next.subcategories = next.subcategories.map((s) =>
    s.categoryId === categoryId && orderById.has(s.id) ? { ...s, sortOrder: orderById.get(s.id) } : s
  );
  return reindexTaxonomySortOrders({ ...draft, ...next });
}
